import { useEffect, useState } from "react";
import { finAPI, fmtINR, fmtDate, STATUS_STYLE } from "./finApi";
import { Plus, X, Undo2, Filter, Check, XCircle, CheckCheck } from "lucide-react";

const REASON_CODES = [
  ["overcharged", "Overcharged"], ["service_issue", "Service issue"],
  ["cancellation", "Cancellation"], ["duplicate", "Duplicate"],
  ["goodwill", "Goodwill"], ["other", "Other"],
];

function CreateDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ payment_id: "", amount: "", reason_code: "other", reason: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await finAPI.createRefund(form);
      onCreated(); onClose();
    } catch (ex) { setErr(ex?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="fin-create-refund-dialog">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display text-[16px] font-semibold text-hocp-navy">Request Refund</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400 hover:text-slate-700"/></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Payment ID *</label>
            <input value={form.payment_id} onChange={e => setForm({...form, payment_id: e.target.value})}
              required data-testid="fin-refund-payment-id" placeholder="Paste payment id from Payments tab"
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-[12px] font-mono"/>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Amount (₹) *</label>
              <input value={form.amount} onChange={e => setForm({...form, amount: e.target.value})}
                required pattern="^\d+(\.\d{1,2})?$" data-testid="fin-refund-amount"
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] font-mono"/>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Reason Code</label>
              <select value={form.reason_code} onChange={e => setForm({...form, reason_code: e.target.value})}
                data-testid="fin-refund-reason-code"
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] bg-white">
                {REASON_CODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Reason *</label>
            <textarea value={form.reason} onChange={e => setForm({...form, reason: e.target.value})}
              rows={3} required data-testid="fin-refund-reason"
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"/>
          </div>
          {err && <div className="text-rose-700 text-[12px] bg-rose-50 px-3 py-2 rounded-lg">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-[13px] font-medium text-slate-600">Cancel</button>
            <button type="submit" disabled={busy} data-testid="fin-refund-submit"
              className="px-5 py-2 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[13px] font-medium disabled:opacity-60">
              {busy ? "Submitting…" : "Request Refund"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function FinanceRefunds() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(false);

  const load = () => {
    finAPI.refunds(status ? { status } : {}).then(setItems).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const act = async (r, kind) => {
    try {
      if (kind === "approve") {
        const notes = window.prompt("Approval notes (optional):") ?? "";
        await finAPI.approveRefund(r.id, { notes });
      } else if (kind === "reject") {
        const reason = window.prompt("Reject reason:"); if (!reason) return;
        await finAPI.rejectRefund(r.id, { reason });
      } else if (kind === "process") {
        const ref = window.prompt("Payout reference / UTR:") ?? "";
        await finAPI.processRefund(r.id, { reference_no: ref, notes: "" });
      }
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="fin-refunds-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-[12px] text-slate-500"><Filter className="w-4 h-4"/> Filter:</div>
        <select value={status} onChange={e => setStatus(e.target.value)} data-testid="fin-refund-filter"
          className="px-3 py-1.5 border border-slate-200 rounded-lg text-[12px] bg-white">
          <option value="">All statuses</option>
          <option value="requested">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="processed">Processed</option>
        </select>
        <div className="flex-1"/>
        <button onClick={() => setOpen(true)} data-testid="fin-new-refund-btn"
          className="px-4 py-2 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[12px] font-medium inline-flex items-center gap-2 hover:brightness-110">
          <Plus className="w-4 h-4"/> Request Refund
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {items.length === 0 ? (
          <div className="p-12 text-center" data-testid="fin-refunds-empty">
            <Undo2 className="w-10 h-10 text-slate-300 mx-auto mb-3"/>
            <div className="text-[13px] text-slate-500">No refunds yet.</div>
          </div>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5">Code</th>
                <th className="text-left px-4 py-2.5">For Payment</th>
                <th className="text-right px-4 py-2.5">Amount</th>
                <th className="text-left px-4 py-2.5">Reason</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-left px-4 py-2.5">Requested</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map(r => {
                const s = STATUS_STYLE[r.status] || { chip: "bg-slate-100 text-slate-700", label: r.status };
                return (
                  <tr key={r.id} className="hover:bg-slate-50" data-testid={`fin-refund-row-${r.code}`}>
                    <td className="px-4 py-2.5 font-mono text-[12px] text-hocp-navy">{r.code}</td>
                    <td className="px-4 py-2.5 font-mono text-[12px] text-slate-600">{r.payment_code || "—"}</td>
                    <td className="px-4 py-2.5 text-right font-medium tabular-nums text-hocp-navy">{fmtINR(r.amount)}</td>
                    <td className="px-4 py-2.5 text-slate-700"><span className="uppercase text-[10px] font-semibold text-slate-500 mr-2">{r.reason_code}</span>{r.reason}</td>
                    <td className="px-4 py-2.5"><span className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium ${s.chip}`}>{s.label}</span></td>
                    <td className="px-4 py-2.5 text-slate-500 text-[12px]">{fmtDate(r.created_at)}</td>
                    <td className="px-4 py-2.5 text-right space-x-2 whitespace-nowrap">
                      {r.status === "requested" && (
                        <>
                          <button onClick={() => act(r, "approve")} data-testid={`fin-refund-approve-${r.code}`}
                            className="text-[12px] text-emerald-700 hover:text-emerald-900 font-medium inline-flex items-center gap-1">
                            <Check className="w-3.5 h-3.5"/>Approve
                          </button>
                          <button onClick={() => act(r, "reject")} data-testid={`fin-refund-reject-${r.code}`}
                            className="text-[12px] text-rose-600 hover:text-rose-800 font-medium inline-flex items-center gap-1">
                            <XCircle className="w-3.5 h-3.5"/>Reject
                          </button>
                        </>
                      )}
                      {r.status === "approved" && (
                        <button onClick={() => act(r, "process")} data-testid={`fin-refund-process-${r.code}`}
                          className="text-[12px] text-sky-700 hover:text-sky-900 font-medium inline-flex items-center gap-1">
                          <CheckCheck className="w-3.5 h-3.5"/>Process
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <CreateDialog open={open} onClose={() => setOpen(false)} onCreated={load}/>
    </div>
  );
}
